
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from '@/integrations/supabase/client';
import { Trash2, AlertTriangle, Save } from 'lucide-react';

interface AccountDeletionSettings {
  auto_deletion_enabled: boolean;
  cooling_off_days: number;
  admin_notification_email: string;
  require_admin_approval: boolean;
}

export const AccountManagementSettings = () => {
  const [settings, setSettings] = useState<AccountDeletionSettings>({
    auto_deletion_enabled: true,
    cooling_off_days: 7,
    admin_notification_email: '',
    require_admin_approval: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('key, value')
        .in('key', [
          'account_deletion_auto_enabled',
          'account_deletion_cooling_off_days',
          'account_deletion_admin_email',
          'account_deletion_require_approval'
        ]);

      if (error) throw error;

      const settingsMap = data?.reduce((acc, item) => {
        acc[item.key] = item.value;
        return acc;
      }, {} as Record<string, any>) || {};

      setSettings({
        auto_deletion_enabled: settingsMap.account_deletion_auto_enabled ?? true,
        cooling_off_days: settingsMap.account_deletion_cooling_off_days ?? 7,
        admin_notification_email: settingsMap.account_deletion_admin_email ?? '',
        require_admin_approval: settingsMap.account_deletion_require_approval ?? false
      });
    } catch (error) {
      console.error('Error fetching account deletion settings:', error);
      toast.error('Failed to load account deletion settings');
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      const settingsToSave = [
        { key: 'account_deletion_auto_enabled', value: settings.auto_deletion_enabled },
        { key: 'account_deletion_cooling_off_days', value: settings.cooling_off_days },
        { key: 'account_deletion_admin_email', value: settings.admin_notification_email },
        { key: 'account_deletion_require_approval', value: settings.require_admin_approval }
      ];

      for (const setting of settingsToSave) {
        const { error } = await supabase
          .from('app_settings')
          .upsert(setting, { onConflict: 'key' });

        if (error) throw error;
      }

      toast.success('Account deletion settings saved successfully');
    } catch (error) {
      console.error('Error saving account deletion settings:', error);
      toast.error('Failed to save account deletion settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-nation-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">Account Management</h2>
        <p className="text-muted-foreground">
          Configure account deletion policies and user data management settings.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Trash2 className="h-5 w-5" />
            Account Deletion Settings
          </CardTitle>
          <CardDescription>
            Configure how user account deletion requests are processed and managed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="auto-deletion" className="text-gray-900 dark:text-gray-100">Enable Automatic Deletion</Label>
              <p className="text-sm text-muted-foreground">
                Automatically process deletion requests after cooling-off period
              </p>
            </div>
            <Switch
              id="auto-deletion"
              checked={settings.auto_deletion_enabled}
              onCheckedChange={(checked) => 
                setSettings(prev => ({ ...prev, auto_deletion_enabled: checked }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cooling-off-days" className="text-gray-900 dark:text-gray-100">Cooling-off Period (Days)</Label>
            <Input
              id="cooling-off-days"
              type="number"
              min="1"
              max="30"
              value={settings.cooling_off_days}
              onChange={(e) => 
                setSettings(prev => ({ ...prev, cooling_off_days: parseInt(e.target.value) || 7 }))
              }
              className="w-32 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            />
            <p className="text-sm text-muted-foreground">
              Users can cancel deletion requests during this period
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-email" className="text-gray-900 dark:text-gray-100">Admin Notification Email</Label>
            <Input
              id="admin-email"
              type="email"
              value={settings.admin_notification_email}
              onChange={(e) => 
                setSettings(prev => ({ ...prev, admin_notification_email: e.target.value }))
              }
              placeholder="admin@example.com"
              className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            />
            <p className="text-sm text-muted-foreground">
              Email address to notify when deletion requests are made
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="require-approval" className="text-gray-900 dark:text-gray-100">Require Admin Approval</Label>
              <p className="text-sm text-muted-foreground">
                All deletion requests must be manually approved by an admin
              </p>
            </div>
            <Switch
              id="require-approval"
              checked={settings.require_admin_approval}
              onCheckedChange={(checked) => 
                setSettings(prev => ({ ...prev, require_admin_approval: checked }))
              }
            />
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button 
              onClick={saveSettings} 
              disabled={isSaving} 
              className="w-full sm:w-auto bg-nation-blue hover:bg-nation-blue/90 text-white"
            >
              {isSaving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Settings</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-orange-200 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
            <AlertTriangle className="h-5 w-5" />
            Data Retention Notice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-orange-700 dark:text-orange-300">
            When users delete their accounts, personal data is anonymized but voting records remain 
            for democratic transparency as required by GDPR. Deleted users appear as "Deleted User" 
            in poll results and comments.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
